import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Award, Wrench, BadgeCheck } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

export default function Competencies() {
  const { user } = useAuthStore();
  const isStaff = ['admin', 'registrar', 'lecturer', 'superadmin'].includes(user?.role || '');
  const [tab, setTab] = useState<'competencies' | 'equipment' | 'certifications'>('competencies');

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Competencies & Trades</h1><p className="page-subtitle">Competency-based assessment, workshop equipment, and trade certifications</p></div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'competencies' ? 'active' : ''}`} onClick={() => setTab('competencies')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Award size={14} />Competencies</span></button>
        <button className={`tab ${tab === 'equipment' ? 'active' : ''}`} onClick={() => setTab('equipment')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Wrench size={14} />Equipment</span></button>
        <button className={`tab ${tab === 'certifications' ? 'active' : ''}`} onClick={() => setTab('certifications')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><BadgeCheck size={14} />Certifications</span></button>
      </div>

      {tab === 'competencies' && <CompetenciesTab isStaff={isStaff} />}
      {tab === 'equipment' && <EquipmentTab isStaff={isStaff} />}
      {tab === 'certifications' && <CertificationsTab isStaff={isStaff} />}
    </div>
  );
}

function CompetenciesTab({ isStaff }: { isStaff: boolean }) {
  const { user } = useAuthStore();
  const isStudent = user?.role === 'student';
  const [items, setItems] = useState<any[]>([]);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [assessFor, setAssessFor] = useState<any>(null);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      if (isStudent) {
        const r = await api.get('/competencies/assessments');
        setAssessments(r.data.data || []);
      } else {
        const r = await api.get('/competencies');
        setItems(r.data.data || []);
      }
    } finally { setLoading(false); }
  }, [isStudent]);

  useEffect(() => { doFetch(); }, [doFetch]);

  if (isStudent) {
    return (
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr><th>Competency</th><th>Unit</th><th>Result</th><th>Assessed</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : assessments.length === 0 ? <tr><td colSpan={4}><div className="empty-state"><Award size={40} /><p>No assessments yet</p></div></td></tr>
              : assessments.map((a: any) => (
                <tr key={a.id}>
                  <td>{a.competency_name}</td>
                  <td>{a.unit_code ? `${a.unit_code} — ${a.unit_name}` : '—'}</td>
                  <td><span className={`badge badge-${a.result === 'competent' ? 'success' : 'warning'}`}>{a.result === 'competent' ? 'Competent' : 'Not Yet Competent'}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(a.assessed_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <>
      {isStaff && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Add Competency</button></div>}
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr><th>Competency</th><th>Unit</th><th>Assessed</th><th>Competent</th>{isStaff && <th>Actions</th>}</tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : items.length === 0 ? <tr><td colSpan={5}><div className="empty-state"><Award size={40} /><p>No competencies defined yet</p></div></td></tr>
              : items.map((c: any) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td>{c.unit_code ? `${c.unit_code} — ${c.unit_name}` : '—'}</td>
                  <td>{c.assessed_count}</td>
                  <td>{c.competent_count}</td>
                  {isStaff && <td><button className="btn btn-secondary btn-sm" onClick={() => setAssessFor(c)}>Assess Student</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <AddCompetencyModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
      {assessFor && <AssessModal competency={assessFor} onClose={() => setAssessFor(null)} onSaved={() => { setAssessFor(null); doFetch(); }} />}
    </>
  );
}

function AddCompetencyModal({ onClose, onSaved }: any) {
  const [units, setUnits] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  useEffect(() => { api.get('/academic/units').then(r => setUnits(r.data.data.units || r.data.data || [])); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.unit_id || !form.name) { toast.error('Unit and name are required'); return; }
    setLoading(true);
    try { await api.post('/competencies', form); toast.success('Competency added'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Add Competency</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Unit *</label><select className="form-input form-select" required value={form.unit_id || ''} onChange={e => set('unit_id', e.target.value)}><option value="">Select unit...</option>{units.map((u: any) => <option key={u.id} value={u.id}>{u.code} — {u.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Competency Name *</label><input className="form-input" required value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Description</label><textarea className="form-input" rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Add'}</button></div>
        </form>
      </div>
    </div>
  );
}

function AssessModal({ competency, onClose, onSaved }: any) {
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ result: 'competent' });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (searchStu.length >= 2) api.get('/students', { params: { search: searchStu, limit: 10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.student_id) { toast.error('Select a student'); return; }
    setLoading(true);
    try { await api.post('/competencies/assessments', { ...form, competency_id: competency.id }); toast.success('Assessment recorded'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Assess — {competency.name}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Student *</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e => setSearchStu(e.target.value)} />
              {stuResults.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id', s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Result *</label><select className="form-input form-select" value={form.result} onChange={e => set('result', e.target.value)}><option value="competent">Competent</option><option value="not_yet_competent">Not Yet Competent</option></select></div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Save Assessment'}</button></div>
        </form>
      </div>
    </div>
  );
}

function EquipmentTab({ isStaff }: { isStaff: boolean }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [bookFor, setBookFor] = useState<any>(null);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/competencies/equipment'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  const conditionColor: Record<string, string> = { good: 'success', fair: 'warning', poor: 'danger', 'under-repair': 'danger' };

  return (
    <>
      {isStaff && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Add Equipment</button></div>}
      {loading ? <div className="card" style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      : items.length === 0 ? <div className="card"><div className="empty-state"><Wrench size={40} /><p>No equipment registered yet</p></div></div>
      : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {items.map((e: any) => (
            <div key={e.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>{e.name}</div>
                <span className={`badge badge-${conditionColor[e.condition] || 'gray'}`} style={{ textTransform: 'capitalize' }}>{e.condition}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{e.category || 'Uncategorized'}{e.location ? ` · ${e.location}` : ''}</div>
              {e.serial_number && <div style={{ fontSize: 12, color: 'var(--text-muted)' }} className="mono">S/N {e.serial_number}</div>}
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 10, width: '100%' }} onClick={() => setBookFor(e)}>Book</button>
            </div>
          ))}
        </div>
      )}
      {showAdd && <AddEquipmentModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
      {bookFor && <BookEquipmentModal equipment={bookFor} onClose={() => setBookFor(null)} onSaved={() => setBookFor(null)} />}
    </>
  );
}

function AddEquipmentModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ condition: 'good' });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try { await api.post('/competencies/equipment', form); toast.success('Equipment added'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Add Equipment</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Name *</label><input className="form-input" required value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Category</label><input className="form-input" value={form.category || ''} onChange={e => set('category', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={form.location || ''} onChange={e => set('location', e.target.value)} /></div>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Serial Number</label><input className="form-input" value={form.serial_number || ''} onChange={e => set('serial_number', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Condition</label><select className="form-input form-select" value={form.condition} onChange={e => set('condition', e.target.value)}><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option><option value="under-repair">Under Repair</option></select></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Add'}</button></div>
        </form>
      </div>
    </div>
  );
}

function BookEquipmentModal({ equipment, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.start_at || !form.end_at) { toast.error('Start and end time are required'); return; }
    setLoading(true);
    try { await api.post('/competencies/equipment/bookings', { ...form, equipment_id: equipment.id }); toast.success('Equipment booked'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Booking failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Book {equipment.name}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Start *</label><input type="datetime-local" className="form-input" required value={form.start_at || ''} onChange={e => set('start_at', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">End *</label><input type="datetime-local" className="form-input" required value={form.end_at || ''} onChange={e => set('end_at', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Purpose</label><input className="form-input" value={form.purpose || ''} onChange={e => set('purpose', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Book'}</button></div>
        </form>
      </div>
    </div>
  );
}

function CertificationsTab({ isStaff }: { isStaff: boolean }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/competencies/certifications'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  return (
    <>
      {isStaff && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Award Certification</button></div>}
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr>{isStaff && <th>Student</th>}<th>Trade</th><th>Level</th><th>Awarding Body</th><th>Cert No.</th><th>Issued</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : items.length === 0 ? <tr><td colSpan={6}><div className="empty-state"><BadgeCheck size={40} /><p>No certifications recorded yet</p></div></td></tr>
              : items.map((c: any) => (
                <tr key={c.id}>
                  {isStaff && <td>{c.student_first_name} {c.student_last_name}</td>}
                  <td style={{ fontWeight: 500 }}>{c.trade}</td>
                  <td>{c.level || '—'}</td>
                  <td>{c.awarding_body || '—'}</td>
                  <td className="mono">{c.certificate_no || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.issue_date ? new Date(c.issue_date).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <AddCertificationModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
    </>
  );
}

function AddCertificationModal({ onClose, onSaved }: any) {
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (searchStu.length >= 2) api.get('/students', { params: { search: searchStu, limit: 10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.student_id || !form.trade) { toast.error('Student and trade are required'); return; }
    setLoading(true);
    try { await api.post('/competencies/certifications', form); toast.success('Certification recorded'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Award Certification</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Student *</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e => setSearchStu(e.target.value)} />
              {stuResults.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id', s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Trade *</label><input className="form-input" required value={form.trade || ''} onChange={e => set('trade', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Level</label><input className="form-input" placeholder="Grade I / II / III" value={form.level || ''} onChange={e => set('level', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Issue Date</label><input type="date" className="form-input" value={form.issue_date || ''} onChange={e => set('issue_date', e.target.value)} /></div>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Awarding Body</label><input className="form-input" placeholder="NITA / KNEC" value={form.awarding_body || ''} onChange={e => set('awarding_body', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Certificate No.</label><input className="form-input" value={form.certificate_no || ''} onChange={e => set('certificate_no', e.target.value)} /></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Award'}</button></div>
        </form>
      </div>
    </div>
  );
}
